export const RTC_SFU_RECONNECTION_STATE = Object.freeze({
  CONNECTED: "connected",
  FAILED: "failed",
  PROVISIONING: "provisioning",
  REALLOCATING: "reallocating",
  RECONNECTING: "reconnecting"
});

export const DEFAULT_RTC_SFU_RECONNECTION_OPTIONS = Object.freeze({
  sameAllocationAttempts: 3,
  freshAllocationAttempts: 2,
  connectAttemptTimeoutMs: 30000,
  recoveryTimeoutMs: 60000,
  admissionTimeoutMs: 10000,
  socketTimeoutMs: 10000,
  mediaSetupTimeoutMs: 20000,
  baseDelayMs: 500,
  maxDelayMs: 3000,
  jitterRatio: 0.25
});

function mergedOptions(options = {}) {
  return {
    ...DEFAULT_RTC_SFU_RECONNECTION_OPTIONS,
    ...options
  };
}

export function calculateRtcSfuReconnectDelay({
  attempt,
  baseDelayMs = DEFAULT_RTC_SFU_RECONNECTION_OPTIONS.baseDelayMs,
  maxDelayMs = DEFAULT_RTC_SFU_RECONNECTION_OPTIONS.maxDelayMs,
  jitterRatio = DEFAULT_RTC_SFU_RECONNECTION_OPTIONS.jitterRatio,
  random = Math.random
}) {
  const exponentialDelay = baseDelayMs * Math.pow(2, Math.max(0, attempt - 1));
  const boundedDelay = Math.min(exponentialDelay, maxDelayMs);
  const jitter = boundedDelay * jitterRatio * (random() * 2 - 1);

  return Math.max(0, Math.round(boundedDelay + jitter));
}

function timeoutError(code, stage, timeoutMs, startedAt) {
  return Object.assign(new Error("RTC SFU signaling connection timed out."), {
    code,
    stage,
    timeoutMs,
    durationMs: Math.max(0, Date.now() - startedAt)
  });
}

function throwIfAborted(signal) {
  if (signal.aborted) throw signal.reason || new Error("RTC SFU recovery cancelled.");
}

function terminal(error) {
  return (
    error?.code === "RTC_SFU_RECOVERY_TIMEOUT" ||
    (error?.stage === "admission" && [401, 403].includes(error.status || error.code))
  );
}

async function connectWithTimeout({ endpoint, connect, options, signal, connectionAttempt, onAttempt }) {
  const controller = new AbortController();
  const startedAt = Date.now();
  let active = true;
  let stage = "admission",
    group,
    phaseStartedAt,
    phaseTimeout,
    attemptTimeout;
  const isCurrent = () => !controller.signal.aborted && !signal.aborted;
  const notify = state => {
    try {
      onAttempt({ stage, state, connectionAttempt, durationMs: Date.now() - startedAt });
    } catch (_) {
      /* Best effort. */
    }
  };
  const onStage = next => {
    if (!active || !isCurrent()) return;
    stage = next;
    const nextGroup = next === "admission" ? "admission" : next === "socket" ? "socket" : "media";
    if (group !== nextGroup) {
      group = nextGroup;
      phaseStartedAt = Date.now();
    }
    clearTimeout(phaseTimeout);
    const budget =
      group === "admission"
        ? options.admissionTimeoutMs
        : group === "socket"
        ? options.socketTimeoutMs
        : options.mediaSetupTimeoutMs;
    phaseTimeout = setTimeout(
      () => controller.abort(timeoutError("RTC_SFU_CONNECTION_TIMEOUT", stage, budget, startedAt)),
      Math.max(0, budget - (Date.now() - phaseStartedAt))
    );
    notify("connecting");
  };
  const parentAbort = () => controller.abort(signal.reason);
  signal.addEventListener("abort", parentAbort, { once: true });
  let abortListener;
  try {
    throwIfAborted(signal);
    const aborted = new Promise((_, reject) => {
      abortListener = () => reject(controller.signal.reason);
      controller.signal.addEventListener("abort", abortListener, { once: true });
    });
    if (options.connectAttemptTimeoutMs > 0) {
      attemptTimeout = setTimeout(
        () =>
          controller.abort(
            timeoutError("RTC_SFU_CONNECTION_TIMEOUT", stage, options.connectAttemptTimeoutMs, startedAt)
          ),
        options.connectAttemptTimeoutMs
      );
    }
    onStage("admission");
    await Promise.race([
      connect(endpoint, { isCurrent, signal: controller.signal, onStage, connectionAttempt }),
      aborted
    ]);
    throwIfAborted(controller.signal);
    notify("connected");
  } catch (error) {
    if (error && !error.stage) error.stage = stage;
    controller.abort(error);
    try {
      onAttempt({
        stage,
        state: "failed",
        connectionAttempt,
        durationMs: Date.now() - startedAt,
        timeoutMs: error?.timeoutMs
      });
    } catch (_) {
      /* Best effort. */
    }
    throw error;
  } finally {
    active = false;
    clearTimeout(phaseTimeout);
    clearTimeout(attemptTimeout);
    signal.removeEventListener("abort", parentAbort);
    controller.signal.removeEventListener("abort", abortListener);
  }
}

export async function recoverRtcSfuConnection({
  endpoint,
  connect,
  resolveFreshEndpoint,
  onStateChange = () => {},
  onAttempt = () => {},
  sleep = delay => new Promise(resolve => setTimeout(resolve, delay)),
  random = Math.random,
  options = {},
  signal: parentSignal
}) {
  const retryOptions = mergedOptions(options);
  const controller = new AbortController();
  const signal = controller.signal;
  const startedAt = Date.now();
  let stage = "admission",
    connectionAttempt = 0,
    timeoutId,
    abortListener;
  const parentAbort = () => controller.abort(parentSignal.reason);
  parentSignal?.addEventListener("abort", parentAbort, { once: true });
  if (parentSignal?.aborted) parentAbort();
  const retry = async (target, attempts) => {
    let lastError;
    for (let attempt = 1; attempt <= attempts; attempt++) {
      throwIfAborted(signal);
      if (attempt > 1) await sleep(calculateRtcSfuReconnectDelay({ attempt: attempt - 1, random, ...retryOptions }));
      throwIfAborted(signal);
      try {
        await connectWithTimeout({
          endpoint: target,
          connect,
          signal,
          options: retryOptions,
          connectionAttempt: ++connectionAttempt,
          onAttempt: summary => {
            stage = summary.stage;
            onAttempt(summary);
          }
        });
        return { endpoint: target };
      } catch (error) {
        if (signal.aborted || terminal(error)) throw error;
        lastError = error;
      }
    }
    throw lastError;
  };
  const run = async () => {
    onStateChange(RTC_SFU_RECONNECTION_STATE.RECONNECTING);
    try {
      return await retry(endpoint, retryOptions.sameAllocationAttempts);
    } catch (error) {
      if (signal.aborted || terminal(error) || !resolveFreshEndpoint) throw error;
    }
    throwIfAborted(signal);
    stage = "allocation";
    onStateChange(RTC_SFU_RECONNECTION_STATE.REALLOCATING);
    const freshEndpoint = await resolveFreshEndpoint({ onStateChange, signal });
    throwIfAborted(signal);
    if (!freshEndpoint) throw new Error("RTC SFU recovery did not resolve an endpoint.");
    return retry(freshEndpoint, retryOptions.freshAllocationAttempts);
  };
  try {
    throwIfAborted(signal);
    const aborted = new Promise((_, reject) => {
      abortListener = () => reject(signal.reason);
      signal.addEventListener("abort", abortListener, { once: true });
    });
    timeoutId = setTimeout(
      () =>
        controller.abort(timeoutError("RTC_SFU_RECOVERY_TIMEOUT", stage, retryOptions.recoveryTimeoutMs, startedAt)),
      retryOptions.recoveryTimeoutMs
    );
    const result = await Promise.race([run(), aborted]);
    throwIfAborted(signal);
    onStateChange(RTC_SFU_RECONNECTION_STATE.CONNECTED);
    return result;
  } catch (error) {
    onStateChange(RTC_SFU_RECONNECTION_STATE.FAILED);
    throw error;
  } finally {
    clearTimeout(timeoutId);
    signal.removeEventListener("abort", abortListener);
    parentSignal?.removeEventListener("abort", parentAbort);
  }
}
